import { useCallback, useEffect, useState } from 'react';
import { getIn, useFormikContext } from 'formik';
import hash from 'object-hash';

import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import FormHelperText from '@mui/material/FormHelperText';

import { EMPTY_ARRAY } from '@e-socna/empty';
import type { Id, RepresentativeTypeId } from '@e-socna/data-layer';

import { CheckboxList } from '../../components/CheckboxList';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useFormikOnNextTick } from '../../hooks/useFormikOnNextTick.hook';

import type { Props } from './types/props.type';
import { InfoAlert } from '../../components/InfoAlert';

export const RepresentativeTypesFormControl = ({
  fallbackTypeIds,
  isInternal,
  name,
  required,
}: Readonly<Props>) => {
  const { errors, setFieldTouched, touched, values } =
    useFormikContext<Readonly<{ _id: Id }>>();

  const { setFieldValue: setFieldValueOnNextTick } = useFormikOnNextTick();

  const formTypeIds = getIn(values, name) as
    readonly RepresentativeTypeId[] | null | undefined;
  const canInherit = !!fallbackTypeIds;
  const mustInherit = canInherit && !formTypeIds;

  const checkboxIdPrefix = `${hash(`${values._id}-${name}`)}-`;

  const [checkedTypeIds, setCheckedTypeIds] = useState(formTypeIds);

  useEffect(() => {
    setFieldValueOnNextTick(name, checkedTypeIds);
  }, [checkedTypeIds, name, setFieldValueOnNextTick]);

  const { representativeTypes } = useReferencesContext();

  const relevantRepresentativeTypes = representativeTypes.filter(
    (representativeType) =>
      isInternal
        ? representativeType.isInternal
        : representativeType.isExternal,
  );

  const customize = useCallback(() => {
    setCheckedTypeIds(fallbackTypeIds);
  }, [fallbackTypeIds, setCheckedTypeIds]);

  const reinitialize = useCallback(() => {
    setCheckedTypeIds(undefined);
  }, [setCheckedTypeIds]);

  return (
    <FormControl
      component="fieldset"
      error={!!(getIn(errors, name) && getIn(touched, name))}
      required={required}
      sx={{ ml: 3, display: 'flex' }}
      variant="standard"
    >
      {canInherit ? (
        <InfoAlert
          action={
            <>
              <Button
                color="inherit"
                onClick={mustInherit ? customize : reinitialize}
                size="small"
              >
                {mustInherit ? 'Spécifier' : 'Réinitialiser'}
              </Button>
            </>
          }
          sx={{ display: 'flex', flex: 1, flexGrow: 1, mt: 1 }}
        >
          {mustInherit
            ? 'Les types sont pour le moment hérités.'
            : 'Les types peuvent être hérités.'}
        </InfoAlert>
      ) : null}

      <FormLabel component="legend">Types</FormLabel>
      <CheckboxList<RepresentativeTypeId>
        idPrefix={checkboxIdPrefix}
        items={relevantRepresentativeTypes.map((item) => ({
          id: item._id,
          label: item.label,
        }))}
        // eslint-disable-next-line react/jsx-no-bind
        onChange={(ids) => {
          setCheckedTypeIds(ids);
          void setFieldTouched(name, true, false);
        }}
        value={(mustInherit ? fallbackTypeIds : checkedTypeIds) ?? EMPTY_ARRAY}
        variant={mustInherit ? 'consultation' : 'edition'}
      />
      {!!(getIn(errors, name) && getIn(touched, name)) && (
        <FormHelperText>{getIn(errors, name)}</FormHelperText>
      )}
    </FormControl>
  );
};
